import {
    CollisionEventType,
    Component,
    Object3D,
    PhysXComponent,
    RayHit,
} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {property} from '@wonderlandengine/api/decorators.js';
import {ControlEvents} from './control-events.js';
import {KeyState} from './control-events.js';
import {PlayerComponent} from './player-component.js';
import {EnemyComponent} from './enemy-component.js';
import {Player, PlayerStates} from '../state/Player.js';
import {gameState} from '../state/GameState.js';
import {Portal} from './portal.js';

export class MovePlayer extends Component {
    static TypeName: string = 'move-player';

    @property.float(10)
    speed!: number;
    @property.float(0.4)
    playerHeight!: number;
    @property.float(20)
    dashSpeed!: number;
    @property.float(0.5)
    dashDuration!: number;
    @property.float(0.5)
    playerRadius!: number;

    private tmpPos = vec3.create();
    private tmpPosOld = vec3.create();
    private tmpVec = vec3.create();
    private tmpVec2 = vec3.create();
    private down = vec3.create();
    private fwd = vec3.create();
    private lastSafePos: vec3 | undefined = undefined;
    private currentState!: string;
    private rayhit: RayHit | undefined = undefined;
    private hittingObject: Object3D | null = null;
    private dash = false;
    private teleportFlag = false;

    start() {
        vec3.set(this.down, 0, -1, 0);

        /* Activate/deactivate based on player alive/dead */
        gameState.currentPlayer.onDie.add(() => (this.active = false));
        gameState.currentPlayer.onRevive.add(() => (this.active = true));

        const pos = gameState.currentPlayer.position;
        if (pos) {
            this.object.setPositionWorld(pos);
            this.object.translateObject([0, this.playerHeight, 0]);
        }
    }

    teleport(pos: number[]) {
        vec3.set(this.tmpPos, pos[0], pos[1], pos[2]);
        this.teleportFlag = true;
    }

    onActivate(): void {
        ControlEvents?.onStateChange.add(this.onStateChange);
        gameState.currentPlayer.onRespawn.add(this.onRespawn);
        ControlEvents.onDashStart.add(this.onDash);
        Portal.onTeleport.add(this.teleport.bind(this));
    }

    onDeactivate(): void {
        ControlEvents?.onStateChange.remove(this.onStateChange);
        ControlEvents.onDashStart.remove(this.onDash);
    }

    onDash = () => {
        if ((gameState.currentPlayer.state & PlayerStates.Idle) != 0) {
            /* Only dash if we're moving in a direction */
            return;
        }
        this.dash = true;
        setTimeout(() => {
            gameState.currentPlayer.setState(PlayerStates.Dashing);
            if (this.currentState === 'notMoving') {
                gameState.currentPlayer.setState(PlayerStates.Idle);
            } else {
                gameState.currentPlayer.setState(PlayerStates.Move);
            }
            this.dash = false;
        }, this.dashDuration * 1000);

        gameState.currentPlayer.setState(PlayerStates.Dashing);
    };

    onStateChange = (currentState: string) => {
        this.currentState = currentState;
        /* Don't set any state when dead or still intializing*/
        if (
            gameState.currentPlayer.state !== PlayerStates.Dead &&
            gameState.currentPlayer.state !== PlayerStates.Dying &&
            gameState.currentPlayer.state !== PlayerStates.Init
        ) {
            if (this.currentState === 'notMoving') {
                gameState.currentPlayer.setState(PlayerStates.Idle);
            } else {
                gameState.currentPlayer.setState(PlayerStates.Move);
            }
        }
    };

    onRespawn() {
        gameState.currentPlayer.setState(PlayerStates.Idle);
        ControlEvents.onStateChange.notify('notMoving');
    }

    checkNavmesh(targetPosition: vec3) {
        vec3.set(
            this.tmpVec2,
            targetPosition[0],
            targetPosition[1] + 0.5,
            targetPosition[2]
        );
        const rayhit = this.engine.physics?.rayCast(this.tmpVec2, this.down, 1)!;
        if (rayhit.hitCount > 0) {
            if (rayhit.objects[0]?.name === 'NavMesh') {
                this.lastSafePos = vec3.clone(targetPosition);
                return true;
            }
        }
        return false;
    }

    update(dt: number) {
        if (
            !this.currentState ||
            gameState.currentPlayer.state === PlayerStates.Dead ||
            gameState.currentPlayer.state === PlayerStates.Dying ||
            gameState.currentPlayer.state === PlayerStates.Init
        ) {
            return;
        }

        if (this.currentState != 'notMoving' || this.dash) {
            if (!this.teleportFlag) {
                this.object.getPositionWorld(this.tmpPos);
            }
            this.object.getForwardWorld(this.fwd);

            let speed = this.dash ? this.dashSpeed : this.speed;

            // Scale the fwd vector by the capsule radius
            const capsuleRadius = this.playerRadius;
            const rayStart = vec3.create();

            vec3.scaleAndAdd(rayStart, this.tmpPos, this.fwd, -capsuleRadius);

            vec3.copy(this.tmpPosOld, this.tmpPos);
            const dist = dt * speed;

            // Move the tmpPos along the fwd direction
            vec3.scaleAndAdd(this.tmpPos, this.tmpPos, this.fwd, dist);

            // check for objstacles 40cm above the ground
            rayStart[1] += 0.4;

            /* Check for obstacles */
            this.rayhit = this.engine.physics?.rayCast(
                rayStart,
                this.fwd,
                0b010 /* Blocked */,
                dist * 8
            );
            let ignoreCollision = false;
            if (this.rayhit!.hitCount > 0 || !this.checkNavmesh(this.tmpPos)) {
                // We hit something or we're not on the navmesh, so don't move

                if (this.rayhit!.distances[0] === 0) {
                    // ray starts inside the object, better to ignore it
                    ignoreCollision = true;
                }

                if (this.rayhit!.normals![0]) {
                    // check if we are faceing the object
                    if (vec3.dot(this.tmpVec!, this.rayhit!.normals[0]) < 0) {
                        return;
                    }
                }
            }

            if (
                ignoreCollision ||
                this.rayhit!.hitCount == 0 ||
                this.rayhit!.objects[0]!.name === 'NavMesh'
            ) {
                this.object.setPositionWorld(this.tmpPos);
                this.object.translateObject([0, this.playerHeight, 0]);
                gameState.currentPlayer.position = this.tmpPos;
            }
        }

        if (this.currentState === 'notMoving') {
            if (!this.teleportFlag) {
                this.object.getPositionWorld(this.tmpPos);
            }
            if (!this.checkNavmesh(this.tmpPos)) {
                if (!this.lastSafePos) {
                    this.object.setPositionWorld([0, 0, 0]);
                } else {
                    // If we're not moving and not on the navmesh, move back to the last safe position
                    this.object.setPositionWorld(this.lastSafePos);
                }
            }
        }

        this.teleportFlag = false;
    }
}
